import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAPI } from "../contexts/APIContext";
import { useAuth } from "../contexts/AuthContext";
import BubbleCanvas from "../components/games/bubble-pop/BubbleCanvas";
import BubbleGameOverModal from "../components/games/bubble-pop/BubbleGameOverModal";
import BackToGamesButton from "../components/games/BackToGamesButton";
import ChallengeBanner, { readChallengeTarget } from "../components/games/ChallengeBanner";
import { useBubbleSoundManager } from "../components/games/bubble-pop/SoundManager";
import { FlameIcon } from "../components/games/ui-icons";
import { buildChallengeLink } from "../utils/challengeLink";
import { setShareMeta } from "../utils/shareMeta";
import {
  BUBBLE_HUES,
  BUBBLE_KINDS,
  MATH_MAX_LIVES,
  MATH_LEVEL_UP_BONUS,
  MATH_RAINBOW_EVERY,
  MATH_ROUND_CLEAR_BONUS,
  MATH_ROUND_TIMEOUT_SECONDS,
  advanceMathBalloons,
  hitTestBubble,
  makeMathDecoy,
  makeMathProblem,
  makeMathTarget,
  mathBubbleSize,
  mathCrossDurationSeconds,
  mathLevelForPopped,
  mathMultiplier,
  mathPopsNeeded,
  mathResultTitle,
  mathSpawnGap,
  mathStreakMilestone,
  mathTargetOnScreen,
  mathTargetShare,
  mathUnlockCopy,
} from "../components/games/bubble-pop/gameUtils";

const F = { fontFamily: "'Fredoka', sans-serif" };

const MATH_TEAL = "#1ABDAD";
const MATH_TEAL_DEEP = "#008F85";
const MISS_RED = "#F04248";
const RAINBOW_CYAN = "#21B3FF";
const MEMORY_PURPLE = "#7A5CF0";

// The waterline: bubbles burst when they touch it. Measured from the top of
// the play field, leaving the sky band for the goal orb.
const SURFACE_Y = 96;

const StarsChip = ({ score }) => (
  <div
    className="flex h-11 items-center gap-2 rounded-[16px] bg-white/95 px-2.5 sm:h-12 sm:min-w-[104px] sm:px-3"
    style={{
      ...F,
      color: "#1A3764",
      border: "1.5px solid rgba(255,255,255,0.96)",
      boxShadow: "0 3px 0 rgba(21,83,145,0.08), 0 6px 14px rgba(11,26,51,0.1)",
    }}
    aria-label={`${score} points`}
  >
    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[10px] bg-[#FFF5CD] sm:h-8 sm:w-8">
      <img src="/assets/pixley_stars.png" alt="" className="h-5 w-5 object-contain sm:h-[22px] sm:w-[22px]" />
    </span>
    <span className="flex min-w-0 flex-col leading-none">
      <span className="hidden text-[9px] font-bold uppercase tracking-[0.12em] text-[#6A7D98] sm:block">
        Points
      </span>
      <span className="mt-0.5 truncate text-[17px] font-bold tabular-nums sm:text-lg">{score}</span>
    </span>
  </div>
);

const LivesChip = ({ lives }) => (
  <div
    className="flex h-11 items-center gap-1 rounded-full bg-white px-3.5 sm:h-12"
    style={{ boxShadow: "0 3px 8px rgba(11,26,51,0.1)" }}
    aria-label={`${lives} of ${MATH_MAX_LIVES} lives remaining`}
  >
    {Array.from({ length: MATH_MAX_LIVES }).map((_, index) => (
      <span
        key={`heart-${index}`}
        className="text-[20px] leading-none transition-all duration-300 sm:text-[22px]"
        style={{
          color: index < lives ? "#FA596B" : "#C6CBD6",
          transform: index < lives ? "scale(1)" : "scale(0.85)",
        }}
      >
        {index < lives ? "♥" : "♡"}
      </span>
    ))}
  </div>
);

// The one thing the whole game reads from: the number to make, in a
// gold-rimmed glass sphere. It bounces on every good pop and splashes in on
// every new number.
const GoalOrb = ({ target, changeKey, pulseKey }) => (
  <div className="relative flex h-[76px] w-[76px] items-center justify-center">
    <span
      key={`splash-${changeKey}`}
      aria-hidden
      className="absolute h-[58px] w-[58px] rounded-full border-[3.5px] math-orb-splash"
      style={{ borderColor: "rgba(255,199,41,0.8)" }}
    />
    <div
      key={`orb-${changeKey}-${pulseKey}`}
      className={`relative flex h-[58px] w-[58px] items-center justify-center rounded-full ${
        pulseKey ? "math-orb-bounce" : "math-orb-in"
      }`}
      style={{
        background: `radial-gradient(circle at 32% 28%, #FFFFFF, ${MATH_TEAL})`,
        border: "4px solid #FFB31A",
        boxShadow: `0 4px 12px ${MATH_TEAL_DEEP}73`,
      }}
    >
      <span
        className="text-[30px] font-bold leading-none text-white tabular-nums"
        style={{ ...F, textShadow: `0 1px 2px ${MATH_TEAL_DEEP}B3` }}
      >
        {target}
      </span>
      <span
        aria-hidden
        className="absolute rounded-full bg-white/55"
        style={{ width: 17, height: 11, left: 9, top: 8, transform: "rotate(-30deg)", filter: "blur(0.8px)" }}
      />
    </div>
  </div>
);

// The waterline itself: two drifting sine crests drawn as repeating SVG bands.
const WaterSurface = ({ surgeKey }) => {
  const wave = (amplitude, y) =>
    `M0 ${y} Q 33 ${y - amplitude} 66 ${y} T 132 ${y} T 198 ${y} T 264 ${y}`;
  return (
    <div
      key={`surge-${surgeKey}`}
      className="pointer-events-none absolute inset-x-0 z-20 math-surface-surge"
      style={{ top: SURFACE_Y - 14, height: 28 }}
    >
      <div className="math-wave-scroll absolute inset-y-0" style={{ width: "200%" }}>
        <svg width="100%" height="28" preserveAspectRatio="none" aria-hidden>
          <defs>
            <pattern id="mathWave" x="0" y="0" width="132" height="28" patternUnits="userSpaceOnUse">
              <path d={wave(5, 14)} fill="none" stroke="rgba(255,255,255,0.95)" strokeWidth="3.5" strokeLinecap="round" />
              <path d={wave(4, 19)} fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5" strokeLinecap="round" />
            </pattern>
          </defs>
          <rect width="100%" height="28" fill="url(#mathWave)" />
        </svg>
      </div>
    </div>
  );
};

const CountdownOverlay = ({ step }) => (
  <div className="absolute inset-0 z-[800] flex items-center justify-center bg-black/20">
    <div
      key={step}
      className="flex h-[128px] w-[128px] items-center justify-center rounded-full math-countdown-pop"
      style={{
        background: `linear-gradient(135deg, ${MATH_TEAL}, ${MATH_TEAL_DEEP})`,
        border: "4px solid rgba(255,255,255,0.8)",
        boxShadow: `0 8px 24px ${MATH_TEAL_DEEP}80`,
      }}
    >
      <span className="font-bold text-white" style={{ ...F, fontSize: step === 0 ? 44 : 62 }}>
        {step === 0 ? "GO!" : step}
      </span>
    </div>
  </div>
);

const LevelFlash = ({ title, subtitle, flashKey }) => (
  <div key={flashKey} className="pointer-events-none absolute inset-0 z-[70] flex items-center justify-center">
    <div className="absolute inset-0 math-level-veil" style={{ background: `linear-gradient(135deg, ${MATH_TEAL}8C, ${MATH_TEAL_DEEP}73)` }} />
    <span
      aria-hidden
      className="absolute h-[300px] w-[300px] rounded-full border-[7px] math-level-ring"
      style={{ borderColor: `${MATH_TEAL}D9` }}
    />
    <div
      className="flex flex-col items-center gap-1.5 rounded-[24px] px-7 py-3.5 text-center text-white math-level-banner"
      style={{
        background: `linear-gradient(135deg, ${MATH_TEAL}, ${MATH_TEAL_DEEP})`,
        border: "2px solid rgba(255,255,255,0.75)",
        boxShadow: `0 6px 16px ${MATH_TEAL_DEEP}8C`,
      }}
    >
      <span className="text-[32px] font-bold leading-none" style={F}>{title}</span>
      <span className="text-[16px] font-semibold leading-snug opacity-95" style={F}>{subtitle}</span>
    </div>
  </div>
);

const INTRO_RISERS = [
  { left: "8%", size: 22, delay: 0, duration: 4.4 },
  { left: "20%", size: 12, delay: 1.6, duration: 3.3 },
  { left: "38%", size: 16, delay: 0.8, duration: 3.8 },
  { left: "66%", size: 13, delay: 2.1, duration: 3.5 },
  { left: "82%", size: 26, delay: 0.4, duration: 4.7 },
  { left: "93%", size: 11, delay: 1.2, duration: 3.1 },
];

// A little glass sum bubble for the intro header — the same look the real
// balloons wear in the water.
const IntroSumBubble = ({ size, base, deep, text }) => (
  <span
    aria-hidden
    className="relative inline-flex items-center justify-center rounded-full"
    style={{
      width: size,
      height: size,
      background: `radial-gradient(circle at 32% 28%, #FFFFFF 0%, ${base} 85%)`,
      border: "3px solid rgba(255,255,255,0.94)",
      boxShadow: `0 7px 16px ${deep}59`,
    }}
  >
    <span
      className="font-bold text-white tabular-nums"
      style={{ ...F, fontSize: Math.round(size * 0.3), textShadow: `0 2px 2px ${deep}BF` }}
    >
      {text}
    </span>
    <span
      className="absolute rounded-full bg-white/60"
      style={{
        width: size * 0.28,
        height: size * 0.17,
        left: size * 0.16,
        top: size * 0.12,
        transform: "rotate(-30deg)",
        filter: "blur(1px)",
      }}
    />
  </span>
);

const INTRO_STEPS = [
  { tint: MATH_TEAL_DEEP, text: "Find the big number at the top." },
  { tint: "#0061E0", text: "Pop every sum that makes it — 3+4 makes 7!" },
  { tint: "#E03D8C", text: "Wrong pops or escapes cost a heart." },
];

const HowToPlayModal = ({ onStart }) => (
  <div
    className="bp-intro-scrim fixed inset-0 z-[900] flex items-center justify-center px-5 py-4"
    role="dialog"
    aria-modal="true"
    aria-labelledby="bubble-math-how-to-title"
  >
    <div className="bp-intro-card max-h-full w-full max-w-[500px] overflow-y-auto rounded-[34px] bg-white">
      {/* Header band: a slice of the game — the goal orb between two sum
          bubbles, tiny bubbles rising through the teal behind them. */}
      <div
        className="relative h-[122px] shrink-0 overflow-hidden"
        style={{ background: `linear-gradient(140deg, ${MATH_TEAL} 0%, ${MATH_TEAL_DEEP} 100%)` }}
      >
        {INTRO_RISERS.map((riser, index) => (
          <span
            key={index}
            aria-hidden
            className="bp-intro-rise absolute rounded-full"
            style={{
              left: riser.left,
              width: riser.size,
              height: riser.size,
              background: "radial-gradient(circle at 32% 28%, rgba(255,255,255,0.85), rgba(255,255,255,0.14))",
              animationDelay: `${riser.delay}s`,
              animationDuration: `${riser.duration}s`,
            }}
          />
        ))}
        <div className="absolute inset-x-0 bottom-[-12px] flex items-end justify-center gap-4">
          <span className="bp-hero-bob" style={{ animationDelay: "0.3s" }}>
            <IntroSumBubble size={58} base="#408FFF" deep="#0061E0" text="3+4" />
          </span>
          <span className="bp-hero-bob mb-2.5" style={{ animationDelay: "0s" }}>
            <span
              aria-hidden
              className="relative inline-flex h-[76px] w-[76px] items-center justify-center rounded-full"
              style={{
                background: `radial-gradient(circle at 32% 28%, #FFFFFF, ${MATH_TEAL})`,
                border: "4px solid #FFB31A",
                boxShadow: `0 6px 14px ${MATH_TEAL_DEEP}80`,
              }}
            >
              <span
                className="text-[34px] font-bold text-white"
                style={{ ...F, textShadow: `0 1px 2px ${MATH_TEAL_DEEP}B3` }}
              >
                7
              </span>
            </span>
          </span>
          <span className="bp-hero-bob" style={{ animationDelay: "0.6s" }}>
            <IntroSumBubble size={54} base="#FC73B8" deep="#E03D8C" text="5+2" />
          </span>
        </div>
      </div>

      <div className="px-6 pb-6 pt-4 text-center sm:px-7">
        <p
          className="bp-intro-item text-[13px] font-bold tracking-[0.2em]"
          style={{ ...F, color: MATH_TEAL_DEEP, animationDelay: "160ms" }}
        >
          MATH BUBBLES
        </p>
        <h2
          id="bubble-math-how-to-title"
          className="bp-intro-item mt-0.5 text-[32px] leading-tight sm:text-[36px]"
          style={{ ...F, color: "#1B2A55", fontWeight: 700, animationDelay: "220ms" }}
        >
          Bubble Pop: Math
        </h2>

        <div className="mt-4 space-y-2.5 text-left">
          {INTRO_STEPS.map((step, index) => (
            <div
              key={step.text}
              className="bp-intro-item flex items-center gap-3.5 rounded-[20px] px-4 py-3"
              style={{
                background: "#F2FBFA",
                border: "2px solid #E2F3F1",
                animationDelay: `${300 + index * 100}ms`,
              }}
            >
              <span
                className="flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-full text-[21px] text-white"
                style={{
                  ...F,
                  fontWeight: 700,
                  background: `radial-gradient(circle at 32% 28%, color-mix(in srgb, ${step.tint} 60%, white), ${step.tint})`,
                  border: "2.5px solid rgba(255,255,255,0.9)",
                  boxShadow: `0 4px 10px ${step.tint}4D`,
                }}
              >
                {index + 1}
              </span>
              <span
                className="text-[18px] leading-snug sm:text-[19px]"
                style={{ ...F, color: "#2A4470", fontWeight: 650 }}
              >
                {step.text}
              </span>
            </div>
          ))}
        </div>

        <div
          className="bp-intro-item mt-2.5 flex items-center justify-center gap-3 rounded-[20px] px-4 py-3"
          style={{ background: "#F8FBFF", border: "2px solid #E8F0FB", animationDelay: "620ms" }}
        >
          <span
            aria-hidden
            className="bp-legend-float relative inline-flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-full"
            style={{
              background:
                "conic-gradient(from 30deg, #FF6B6B, #FF9E33, #FFD84D, #45CC73, #21B3FF, #408FFF, #9E6BF5, #FF6B6B)",
              border: "3px solid rgba(255,255,255,0.9)",
              boxShadow: "0 5px 14px rgba(122,92,240,0.4)",
            }}
          >
            <span className="text-[17px] font-bold text-white" style={F}>✦</span>
          </span>
          <span className="text-[18px] leading-snug sm:text-[19px]" style={{ ...F, color: "#2A4470", fontWeight: 650 }}>
            Tap the rainbow — it pops every match!
          </span>
        </div>

        <button
          type="button"
          onClick={onStart}
          autoFocus
          className="bp-intro-cta mt-4 h-[60px] w-full rounded-full text-[20px] font-bold text-white transition hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98]"
          style={{
            ...F,
            background: `linear-gradient(135deg, ${MATH_TEAL}, ${MATH_TEAL_DEEP})`,
            boxShadow: `0 5px 0 #00726A, 0 12px 22px ${MATH_TEAL_DEEP}3D`,
          }}
        >
          Let&apos;s count!&nbsp;&nbsp;<span className="bp-cta-arrow inline-block">→</span>
        </button>
      </div>
    </div>
  </div>
);

const createEngine = (area = { width: 0, height: 0 }) => ({
  area,
  balloons: [],
  running: false,
  elapsed: 0,
  roundElapsed: 0,
  frameAccumulator: 0,
  lastFrame: 0,
  spawnCooldown: 0,
  spawnCount: 0,
  score: 0,
  streak: 0,
  bestStreak: 0,
  lives: MATH_MAX_LIVES,
  level: 1,
  poppedCount: 0,
  roundPops: 0,
  target: 7,
  shownRainbowHint: false,
  lastHue: null,
  lastLaneX: -1000,
});

// Bubble Pop: Math — the number chase, ported 1:1 from the iOS
// BubblePopMathGameView. A big number sits at the top; bubbles rise through
// the water carrying sums. Tap every bubble that makes the number. Wrong taps
// and escaped matches cost a heart; rounds wash the board clean; levels
// unlock take-away, times tables and fair shares.
const MathBubblePopPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { apiClient } = useAPI();
  const { user, isAuthenticated, refreshUserData } = useAuth();
  const { play } = useBubbleSoundManager();

  const [status, setStatus] = useState("intro"); // intro | countdown | playing | ended
  // A friend's beat-my-score link (?challenge=N) — read once on mount.
  const [challengeTarget] = useState(readChallengeTarget);
  const [countdownStep, setCountdownStep] = useState(null);
  const [balloons, setBalloons] = useState([]);
  const [effects, setEffects] = useState([]);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [lives, setLives] = useState(MATH_MAX_LIVES);
  const [level, setLevel] = useState(1);
  const [target, setTarget] = useState(7);
  const [targetChangeKey, setTargetChangeKey] = useState(0);
  const [targetPulseKey, setTargetPulseKey] = useState(0);
  const [surgeKey, setSurgeKey] = useState(0);
  const [comboBanner, setComboBanner] = useState(null);
  const [levelFlash, setLevelFlash] = useState(null);
  const [resetSignal, setResetSignal] = useState(0);
  const [celebrationSignal, setCelebrationSignal] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [poppedCount, setPoppedCount] = useState(0);
  const [survivedSeconds, setSurvivedSeconds] = useState(0);
  const [starsAwarded, setStarsAwarded] = useState(0);
  const [challengeCopied, setChallengeCopied] = useState(false);

  const engineRef = useRef(createEngine());
  const balloonIdRef = useRef(0);
  const effectIdRef = useRef(0);
  const bannerIdRef = useRef(0);
  const effectTimeoutsRef = useRef(new Set());
  const bannerTimeoutRef = useRef(null);
  const levelFlashTimeoutRef = useRef(null);
  const countdownTimeoutsRef = useRef(new Set());
  const hasSavedResultRef = useRef(false);

  const clearEffects = useCallback(() => {
    effectTimeoutsRef.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
    effectTimeoutsRef.current.clear();
    setEffects([]);
  }, []);

  const addEffect = useCallback(({ x, y, size = 70 }, text, color, big = false) => {
    effectIdRef.current += 1;
    const effect = { id: `math-fx-${effectIdRef.current}`, x, y, size, text, color, big };
    setEffects((current) => [...current, effect]);
    const timeoutId = window.setTimeout(() => {
      effectTimeoutsRef.current.delete(timeoutId);
      setEffects((current) => current.filter((item) => item.id !== effect.id));
    }, 760);
    effectTimeoutsRef.current.add(timeoutId);
  }, []);

  const showBanner = useCallback((text, celebrate = false) => {
    if (bannerTimeoutRef.current) window.clearTimeout(bannerTimeoutRef.current);
    bannerIdRef.current += 1;
    setComboBanner({ key: bannerIdRef.current, text });
    if (celebrate) setCelebrationSignal((signal) => signal + 1);
    bannerTimeoutRef.current = window.setTimeout(() => {
      setComboBanner(null);
      bannerTimeoutRef.current = null;
    }, 950);
  }, []);

  useEffect(
    () => () => {
      engineRef.current.running = false;
      effectTimeoutsRef.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
      countdownTimeoutsRef.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
      if (bannerTimeoutRef.current) window.clearTimeout(bannerTimeoutRef.current);
      if (levelFlashTimeoutRef.current) window.clearTimeout(levelFlashTimeoutRef.current);
    },
    []
  );

  const persistResultOnce = useCallback(
    async (finalScore) => {
      // Guests play free: nothing to save, nothing to award.
      if (!isAuthenticated) return;
      if (hasSavedResultRef.current || !apiClient) return;
      hasSavedResultRef.current = true;
      try {
        const response = await apiClient.postGameProgress("BUBBLE_POP_MATH", finalScore, true, null);
        if (response?.status || response?.success) {
          setStarsAwarded(Number(response?.stars_awarded || 0));
          window.dispatchEvent(new CustomEvent("games:progress-updated"));
          refreshUserData?.();
        }
      } catch {
        // Finishing the game should never be blocked by a progress-save failure.
      }
    },
    [apiClient, isAuthenticated, refreshUserData]
  );

  const finishGame = useCallback(() => {
    const engine = engineRef.current;
    if (!engine.running) return;
    engine.running = false;
    engine.balloons = [];
    setBalloons([]);
    setBestStreak(engine.bestStreak);
    setPoppedCount(engine.poppedCount);
    setSurvivedSeconds(Math.floor(engine.elapsed));
    setStatus("ended");
    play("gameover");
    persistResultOnce(engine.score);
  }, [persistResultOnce, play]);

  // A bubble that makes the number — never repeating a sum already floating.
  const matchProblem = useCallback(() => {
    const engine = engineRef.current;
    const onScreen = engine.balloons.map((balloon) => balloon.problem).filter(Boolean);
    for (let attempt = 0; attempt < 5; attempt += 1) {
      const candidate = makeMathProblem({ answer: engine.target, level: engine.level });
      if (!candidate) break;
      if (!onScreen.some((problem) => problem.text === candidate.text)) return candidate;
    }
    return (
      makeMathProblem({ answer: engine.target, level: engine.level }) || {
        text: `1+${engine.target - 1}`,
        answer: engine.target,
      }
    );
  }, []);

  const decoyProblem = useCallback(() => {
    const engine = engineRef.current;
    const onScreen = engine.balloons.map((balloon) => balloon.problem).filter(Boolean);
    for (let attempt = 0; attempt < 5; attempt += 1) {
      const candidate = makeMathDecoy({
        target: engine.target,
        level: engine.level,
        tricky: engine.level >= 3,
      });
      if (!onScreen.some((problem) => problem.text === candidate.text)) return candidate;
    }
    return makeMathDecoy({ target: engine.target, level: engine.level, tricky: engine.level >= 3 });
  }, []);

  const spawnBalloon = useCallback(() => {
    const engine = engineRef.current;
    engine.spawnCount += 1;

    // The board must always offer a way to score, so a field with no match on
    // it forces the next bubble to be one.
    const hasMatch = engine.balloons.some((balloon) => balloon.problem?.answer === engine.target);
    const wantsRainbow = hasMatch && engine.spawnCount % MATH_RAINBOW_EVERY === 0;
    let problem;
    if (wantsRainbow) {
      problem = null;
    } else if (!hasMatch || Math.random() < mathTargetShare(engine.level)) {
      problem = matchProblem();
    } else {
      problem = decoyProblem();
    }

    const spawnY = engine.area.height;
    const fieldHeight = Math.max(1, spawnY - SURFACE_Y);
    const size = mathBubbleSize({ problem, fieldHeight });
    const half = size / 2;

    // Keep consecutive bubbles horizontally apart so they rise as a loose
    // stagger, never a stack.
    const maxX = Math.max(half, engine.area.width - half);
    let x = half + Math.random() * (maxX - half);
    for (let attempt = 0; attempt < 8 && Math.abs(x - engine.lastLaneX) < size + 10; attempt += 1) {
      x = half + Math.random() * (maxX - half);
    }
    engine.lastLaneX = x;

    // Born fully clear of the bottom edge; speed comes from the actual trip so
    // a bubble takes the same time to reach the surface on every screen size.
    const startY = spawnY - half - 6;
    const travel = Math.max(1, startY - (SURFACE_Y + half));
    const speed = travel / mathCrossDurationSeconds(engine.level);

    // Consecutive bubbles never share a colour — colour is decoration here;
    // the sum is the only thing worth reading.
    const hueChoices = BUBBLE_HUES.filter((hue) => hue.name !== engine.lastHue?.name);
    const hue = hueChoices[Math.floor(Math.random() * hueChoices.length)];
    engine.lastHue = hue;

    balloonIdRef.current += 1;
    engine.balloons = [
      ...engine.balloons,
      {
        id: `math-balloon-${balloonIdRef.current}`,
        kind: problem ? BUBBLE_KINDS.NORMAL : BUBBLE_KINDS.RAINBOW,
        problem,
        tapHint: !problem,
        hue,
        x,
        y: startY,
        size,
        speed,
        drift: -18 + Math.random() * 36,
        wobblePhase: Math.random() * Math.PI * 2,
        age: 0,
      },
    ];
    setBalloons([...engine.balloons]);

    if (!problem && !engine.shownRainbowHint) {
      engine.shownRainbowHint = true;
      showBanner("Tap the rainbow!");
    }
  }, [decoyProblem, matchProblem, showBanner]);

  // A new number always lands on an empty field. Everything left over poofs
  // away for free.
  const newRound = useCallback(
    (cleared, quietly = false) => {
      const engine = engineRef.current;
      engine.balloons.forEach((balloon) => addEffect(balloon, null, "rgba(255,255,255,0.9)"));
      engine.balloons = [];
      engine.roundPops = 0;
      engine.roundElapsed = 0;
      engine.lastLaneX = -1000;
      engine.spawnCooldown = 0.4;
      setBalloons([]);
      setSurgeKey((key) => key + 1);

      if (cleared) {
        engine.score += MATH_ROUND_CLEAR_BONUS;
        setScore(engine.score);
        if (!quietly) setCelebrationSignal((signal) => signal + 1);
        play("combo");
      }
      engine.target = makeMathTarget({ level: engine.level, avoiding: engine.target });
      setTarget(engine.target);
      setTargetChangeKey((key) => key + 1);
    },
    [addEffect, play]
  );

  const levelUpIfNeeded = useCallback(() => {
    const engine = engineRef.current;
    const targetLevel = mathLevelForPopped(engine.poppedCount);
    if (targetLevel <= engine.level) return false;
    engine.level = targetLevel;
    engine.score += MATH_LEVEL_UP_BONUS;
    setLevel(targetLevel);
    setScore(engine.score);
    const copy = mathUnlockCopy(targetLevel);
    setLevelFlash({ key: targetLevel, title: `LEVEL ${targetLevel}!`, subtitle: `${copy.title}  ${copy.subtitle}` });
    setCelebrationSignal((signal) => signal + 1);
    if (levelFlashTimeoutRef.current) window.clearTimeout(levelFlashTimeoutRef.current);
    levelFlashTimeoutRef.current = window.setTimeout(() => {
      setLevelFlash(null);
      levelFlashTimeoutRef.current = null;
    }, 1600);
    play("victory");
    return true;
  }, [play]);

  const streakMilestone = useCallback(() => {
    const engine = engineRef.current;
    const word = mathStreakMilestone(engine.streak);
    if (!word) return;
    showBanner(word, true);
    play("combo");
  }, [play, showBanner]);

  // The good moment: that sum made the number.
  const popMatch = useCallback(
    (balloon) => {
      const engine = engineRef.current;
      engine.balloons = engine.balloons.filter((item) => item.id !== balloon.id);
      engine.streak += 1;
      engine.bestStreak = Math.max(engine.bestStreak, engine.streak);
      const mult = mathMultiplier(engine.streak);
      const gained = 10 * mult;
      engine.score += gained;
      engine.poppedCount += 1;
      engine.roundPops += 1;

      setBalloons([...engine.balloons]);
      setStreak(engine.streak);
      setScore(engine.score);
      setTargetPulseKey((key) => key + 1);
      addEffect(balloon, `+${gained}`, balloon.hue.deep, mult >= 2);
      play("pop");

      streakMilestone();
      const leveled = levelUpIfNeeded();
      if (engine.roundPops >= mathPopsNeeded(engine.level)) newRound(true, leveled);
    },
    [addEffect, levelUpIfNeeded, newRound, play, streakMilestone]
  );

  // That sum didn't make the number — a heart, and the streak.
  const popWrong = useCallback(
    (balloon) => {
      const engine = engineRef.current;
      engine.balloons = engine.balloons.filter((item) => item.id !== balloon.id);
      engine.streak = 0;
      engine.lives = Math.max(0, engine.lives - 1);
      setBalloons([...engine.balloons]);
      setStreak(0);
      setLives(engine.lives);
      setResetSignal((signal) => signal + 1);
      setSurgeKey((key) => key + 1);
      addEffect(balloon, "-1", MISS_RED, true);
      play("wrong");
      if (engine.lives <= 0) finishGame();
    },
    [addEffect, finishGame, play]
  );

  // The rainbow does the round's work for you: every matching bubble pops for
  // full points, and the rest of the board poofs away safely.
  const rainbowClear = useCallback(
    (rainbow) => {
      const engine = engineRef.current;
      engine.balloons = engine.balloons.filter((item) => item.id !== rainbow.id);
      addEffect(rainbow, null, RAINBOW_CYAN, true);
      showBanner("RAINBOW POP!", true);
      setResetSignal((signal) => signal + 1);
      setSurgeKey((key) => key + 1);
      play("combo");

      const mult = mathMultiplier(engine.streak);
      let gained = 0;
      engine.balloons.forEach((balloon) => {
        if (balloon.problem?.answer === engine.target) {
          gained += 10 * mult;
          engine.poppedCount += 1;
          engine.roundPops += 1;
          addEffect(balloon, null, balloon.hue.base);
        } else {
          addEffect(balloon, null, "rgba(255,255,255,0.9)"); // safe poof
        }
      });
      engine.balloons = [];
      engine.score += gained;
      setBalloons([]);
      setScore(engine.score);
      if (gained > 0) {
        addEffect(
          { x: engine.area.width / 2, y: (SURFACE_Y + engine.area.height) / 2, size: 90 },
          `+${gained}`,
          MEMORY_PURPLE,
          true
        );
      }

      const leveled = levelUpIfNeeded();
      if (engine.roundPops >= mathPopsNeeded(engine.level)) newRound(true, leveled);
    },
    [addEffect, levelUpIfNeeded, newRound, play, showBanner]
  );

  const registerMiss = useCallback(
    (count) => {
      const engine = engineRef.current;
      engine.streak = 0;
      engine.lives = Math.max(0, engine.lives - 1);
      setStreak(0);
      setLives(engine.lives);
      setResetSignal((signal) => signal + 1);
      setSurgeKey((key) => key + 1);
      addEffect(
        { x: engine.area.width / 2, y: SURFACE_Y + 46, size: 80 },
        count > 1 ? `MISSED ${count}!` : "MISSED!",
        MISS_RED,
        true
      );
      play("wrong");
      if (engine.lives <= 0) finishGame();
    },
    [addEffect, finishGame, play]
  );

  const handlePopAt = useCallback(
    (point) => {
      const engine = engineRef.current;
      if (!engine.running) return;
      const id = hitTestBubble(engine.balloons, point);
      if (!id) return;
      const balloon = engine.balloons.find((item) => item.id === id);
      if (!balloon) return;
      if (!balloon.problem) {
        rainbowClear(balloon);
      } else if (balloon.problem.answer === engine.target) {
        popMatch(balloon);
      } else {
        popWrong(balloon);
      }
    },
    [popMatch, popWrong, rainbowClear]
  );

  const tick = useCallback(
    (dt) => {
      const engine = engineRef.current;
      if (!engine.running || engine.area.width <= 0 || engine.area.height <= SURFACE_Y) return;

      engine.elapsed += dt;
      engine.roundElapsed += dt;

      // A round starved of matches rolls over on its own — silently, and
      // without costing anything.
      if (engine.roundElapsed > MATH_ROUND_TIMEOUT_SECONDS) newRound(false);

      // Keep the water stocked to the level's bubble count, one at a time.
      engine.spawnCooldown -= dt;
      if (engine.balloons.length < mathTargetOnScreen(engine.level) && engine.spawnCooldown <= 0) {
        spawnBalloon();
        engine.spawnCooldown = mathSpawnGap(engine.level);
      }

      // Move; a bubble that touches the surface bursts there.
      const moved = advanceMathBalloons(engine.balloons, dt, engine.area, SURFACE_Y);
      engine.balloons = moved.balloons;
      setBalloons([...engine.balloons]);
      if (!moved.surfaced.length) return;

      // One heart per surfacing EVENT — a cluster breaking through together
      // should never wipe several lives in a single instant.
      let missed = 0;
      moved.surfaced.forEach((balloon) => {
        const isMatch = Boolean(balloon.problem) && balloon.problem.answer === engine.target;
        if (isMatch) missed += 1;
        addEffect(
          { x: balloon.x, y: SURFACE_Y, size: balloon.size },
          null,
          isMatch ? MISS_RED : balloon.problem ? balloon.hue.base : RAINBOW_CYAN
        );
      });
      if (missed > 0) {
        registerMiss(missed);
      } else {
        play("miss");
      }
    },
    [addEffect, newRound, play, registerMiss, spawnBalloon]
  );

  useEffect(() => {
    let animationFrameId;
    const frame = (timestamp) => {
      const engine = engineRef.current;
      if (engine.running) {
        if (!engine.lastFrame) engine.lastFrame = timestamp;
        const frameDt = Math.min(0.05, Math.max(0, (timestamp - engine.lastFrame) / 1000));
        engine.lastFrame = timestamp;
        engine.frameAccumulator += frameDt;
        const fixedStep = 1 / 60;
        while (engine.frameAccumulator >= fixedStep) {
          tick(fixedStep);
          engine.frameAccumulator -= fixedStep;
        }
      } else {
        engine.lastFrame = timestamp;
      }
      animationFrameId = window.requestAnimationFrame(frame);
    };
    animationFrameId = window.requestAnimationFrame(frame);
    return () => window.cancelAnimationFrame(animationFrameId);
  }, [tick]);

  const handleAreaChange = useCallback((area) => {
    engineRef.current.area = area;
  }, []);

  const startGame = useCallback(() => {
    const area = engineRef.current.area;
    clearEffects();
    if (bannerTimeoutRef.current) window.clearTimeout(bannerTimeoutRef.current);
    setComboBanner(null);
    setLevelFlash(null);

    const fresh = createEngine(area);
    fresh.running = true;
    fresh.lastFrame = performance.now();
    fresh.target = makeMathTarget({ level: 1, avoiding: null });
    engineRef.current = fresh;
    hasSavedResultRef.current = false;

    setBalloons([]);
    setScore(0);
    setStreak(0);
    setLives(MATH_MAX_LIVES);
    setLevel(1);
    setTarget(fresh.target);
    setTargetChangeKey((key) => key + 1);
    setBestStreak(0);
    setPoppedCount(0);
    setSurvivedSeconds(0);
    setStarsAwarded(0);
    setStatus("playing");
  }, [clearEffects]);

  // Ready… set… POP! A short 3-2-1 so the child settles before bubble one.
  const startWithCountdown = useCallback(() => {
    countdownTimeoutsRef.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
    countdownTimeoutsRef.current.clear();
    setStatus("countdown");
    setBalloons([]);
    [3, 2, 1].forEach((step, index) => {
      const timeoutId = window.setTimeout(() => setCountdownStep(step), index * 600);
      countdownTimeoutsRef.current.add(timeoutId);
    });
    const goId = window.setTimeout(() => setCountdownStep(0), 1800);
    countdownTimeoutsRef.current.add(goId);
    const startId = window.setTimeout(() => {
      setCountdownStep(null);
      startGame();
    }, 2250);
    countdownTimeoutsRef.current.add(startId);
  }, [startGame]);

  const handleStartGame = useCallback(() => {
    play("click");
    startWithCountdown();
  }, [play, startWithCountdown]);

  const handleBackToGames = useCallback(() => {
    engineRef.current.running = false;
    play("click");
    // Guests can't enter the protected games hub - send them home instead of
    // bouncing them into the auth wall.
    navigate(isAuthenticated ? "/games" : "/");
  }, [isAuthenticated, navigate, play]);

  const handleSignup = useCallback(() => {
    engineRef.current.running = false;
    navigate(`/auth?redirect=${encodeURIComponent("/games/bubble-pop")}`);
  }, [navigate]);

  // Share YOUR score as a new challenge. ref is the signed-in player's id;
  // guests share without one (never forward someone else's ref).
  const handleChallengeShare = useCallback(() => {
    const link = buildChallengeLink("/games/bubble-pop", score, user?.id);
    const text = `Can you beat ${score} in Bubble Pop Math on Pixley? Play here: ${link}`;
    if (navigator.share) {
      navigator.share({ text }).catch(() => {});
      return;
    }
    navigator.clipboard?.writeText(text).then(() => {
      setChallengeCopied(true);
      window.setTimeout(() => setChallengeCopied(false), 2000);
    });
  }, [score, user]);

  // Challenge links get their own share preview.
  useEffect(() => {
    if (challengeTarget) {
      setShareMeta({
        title: `Beat ${challengeTarget} in Bubble Pop Math - Pixley`,
        description: "Pop the bubbles that make the number. Play free, no download needed.",
      });
    }
  }, [challengeTarget]);

  return (
    <div
      className="relative min-h-screen px-4 pb-8 pt-28 sm:px-6"
      style={{ ...F, background: "linear-gradient(180deg, #EFFBFA 0%, #D3F2EF 100%)" }}
    >
      <ChallengeBanner target={challengeTarget} score={score} ended={status === "ended"} />
      <div className="mx-auto w-full max-w-[1100px]">
        <header
          className="mb-3 grid grid-cols-2 items-center gap-2 rounded-[24px] p-2.5 sm:mb-4 sm:grid-cols-[1fr_auto_1fr] sm:gap-4 sm:p-3"
          style={{
            background: "rgba(240,251,250,0.85)",
            border: "1.5px solid rgba(255,255,255,0.9)",
            boxShadow: `0 5px 18px ${MATH_TEAL_DEEP}1F`,
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
          }}
        >
          <div className="col-start-1 row-start-2 justify-self-start sm:row-start-1">
            <BackToGamesButton onClick={handleBackToGames} accent={MATH_TEAL_DEEP} />
          </div>

          <div className="col-span-2 row-start-1 min-w-0 text-center sm:col-span-1 sm:col-start-2">
            <h1
              className="text-[28px] leading-none sm:text-[34px]"
              style={{ color: MATH_TEAL_DEEP, fontWeight: 700, letterSpacing: "-0.025em" }}
            >
              Bubble Pop: Math
            </h1>
            <p className="mt-1.5 text-[13px] sm:text-sm" style={{ color: "#5D7695", fontWeight: 600 }}>
              Pop every bubble that makes the number!
            </p>
          </div>

          <div className="col-start-2 row-start-2 flex items-center justify-self-end gap-1.5 sm:col-start-3 sm:row-start-1 sm:gap-2">
            <div
              className="flex h-11 items-center rounded-full bg-white px-3.5 text-[15px] font-bold sm:h-12"
              style={{ color: MATH_TEAL_DEEP, boxShadow: "0 3px 8px rgba(11,26,51,0.1)" }}
            >
              Lv {level}
            </div>
            <LivesChip lives={lives} />
            <StarsChip score={score} />
            {challengeTarget && (
              <div
                className="flex h-11 items-center gap-1.5 rounded-full px-3.5 text-[15px] font-bold text-white sm:h-12"
                style={{
                  ...F,
                  background: `linear-gradient(135deg, ${MATH_TEAL} 0%, ${MATH_TEAL_DEEP} 100%)`,
                  boxShadow: "0 3px 8px rgba(11,26,51,0.18)",
                }}
                aria-label={`Score to beat: ${challengeTarget}`}
              >
                Beat {challengeTarget}!
              </div>
            )}
          </div>
        </header>

        <div
          className="relative h-[calc(100dvh-16rem)] min-h-[500px] max-h-[740px] w-full overflow-hidden rounded-[28px] sm:h-[calc(100dvh-13rem)]"
          style={{
            border: "3px solid rgba(255,255,255,0.92)",
            boxShadow: `0 8px 0 ${MATH_TEAL_DEEP}14, 0 20px 38px ${MATH_TEAL_DEEP}29`,
          }}
        >
          <BubbleCanvas
            bubbles={balloons}
            effects={effects}
            onPop={null}
            onPopAt={handlePopAt}
            onAreaChange={handleAreaChange}
            comboBanner={comboBanner}
            resetSignal={resetSignal}
            celebrationSignal={celebrationSignal}
            isFever={false}
            isGoldRush={false}
            superCharge={0}
            onSuperPop={null}
            isRunning={status === "playing"}
            popInDuration={0.34}
            popInFrom={0.12}
            popOnMove={false}
            background={`linear-gradient(180deg, #FDFFFF 0%, #E9FAF8 ${SURFACE_Y}px, ${MATH_TEAL}59 ${
              SURFACE_Y + 8
            }px, ${MATH_TEAL}8C 55%, ${MATH_TEAL_DEEP}A6 100%)`}
          >
            {/* Sky band: the goal orb floats above the waterline. */}
            <div
              className="pointer-events-none absolute inset-x-0 top-0 z-40 flex items-start justify-center"
              style={{ height: SURFACE_Y }}
            >
              <div className="flex items-center gap-3 pt-1.5">
                <GoalOrb target={target} changeKey={targetChangeKey} pulseKey={targetPulseKey} />
                {streak >= 2 && (
                  <div
                    key={streak}
                    className="rounded-full px-4 py-1.5 text-sm text-white tabular-nums sm:text-base"
                    style={{
                      fontWeight: 700,
                      background: "linear-gradient(180deg, #FFC729 0%, #FFB31A 100%)",
                      border: "1.5px solid rgba(255,255,255,0.85)",
                      boxShadow: "0 4px 10px rgba(255,179,26,0.35)",
                      transform: `scale(${1 + Math.min(streak, 10) * 0.02})`,
                    }}
                  >
                    <FlameIcon size={14} color="#FFF" /> ×{mathMultiplier(streak)}
                  </div>
                )}
              </div>
            </div>

            <WaterSurface surgeKey={surgeKey} />

            {levelFlash && (
              <LevelFlash flashKey={levelFlash.key} title={levelFlash.title} subtitle={levelFlash.subtitle} />
            )}
          </BubbleCanvas>

          {status === "countdown" && countdownStep !== null && <CountdownOverlay step={countdownStep} />}

          {status === "intro" && <HowToPlayModal onStart={handleStartGame} />}

          <BubbleGameOverModal
            open={status === "ended"}
            reason="math"
            accent={MATH_TEAL}
            accentDeep={MATH_TEAL_DEEP}
            title={mathResultTitle(poppedCount)}
            score={score}
            starsAwarded={starsAwarded}
            survivedSeconds={survivedSeconds}
            bestCombo={bestStreak}
            solved={poppedCount}
            onRetry={handleStartGame}
            onBack={handleBackToGames}
            challengeTarget={challengeTarget}
            isGuest={!isAuthenticated}
            onSignup={handleSignup}
            onChallengeShare={score > 0 ? handleChallengeShare : null}
            challengeCopied={challengeCopied}
          />
        </div>
      </div>

      <style>{`
        @keyframes mathWaveScroll {
          from { transform: translateX(0); }
          to { transform: translateX(-132px); }
        }
        .math-wave-scroll { animation: mathWaveScroll 2.6s linear infinite; }
        @keyframes mathSurfaceSurge {
          0% { transform: scaleY(1); }
          25% { transform: scaleY(2.1); }
          100% { transform: scaleY(1); }
        }
        .math-surface-surge { transform-origin: center; animation: mathSurfaceSurge 700ms ease-out; }
        @keyframes mathOrbBounce {
          0% { transform: scale(1); }
          30% { transform: scale(1.16); }
          100% { transform: scale(1); }
        }
        .math-orb-bounce { animation: mathOrbBounce 300ms ease-out; }
        @keyframes mathOrbIn {
          0% { transform: scale(0.55); }
          60% { transform: scale(1.12); }
          100% { transform: scale(1); }
        }
        .math-orb-in { animation: mathOrbIn 440ms cubic-bezier(.2,.9,.3,1.2); }
        @keyframes mathOrbSplash {
          0% { transform: scale(1); opacity: 0.9; }
          100% { transform: scale(1.9); opacity: 0; }
        }
        .math-orb-splash { animation: mathOrbSplash 750ms ease-out forwards; }
        @keyframes mathCountdownPop {
          0% { transform: scale(0.3) rotate(-10deg); opacity: 0; }
          100% { transform: scale(1) rotate(0); opacity: 1; }
        }
        .math-countdown-pop { animation: mathCountdownPop 320ms cubic-bezier(.2,.9,.3,1.25); }
        @keyframes mathLevelVeil {
          0% { opacity: 1; }
          100% { opacity: 0; }
        }
        .math-level-veil { animation: mathLevelVeil 1000ms ease-out forwards; }
        @keyframes mathLevelRing {
          0% { transform: scale(0.3); opacity: 0.95; }
          100% { transform: scale(4.6); opacity: 0; }
        }
        .math-level-ring { animation: mathLevelRing 1000ms ease-out forwards; }
        @keyframes mathLevelBanner {
          0% { transform: scale(0.4) rotate(-6deg); opacity: 0; }
          60% { transform: scale(1.06) rotate(1deg); opacity: 1; }
          80%, 100% { transform: scale(1) rotate(0); opacity: 1; }
        }
        .math-level-banner { animation: mathLevelBanner 600ms cubic-bezier(.2,.9,.3,1.2) 50ms both; }

        /* --- Intro card (shared look with Bubble Pop's) --- */
        @keyframes bpIntroScrimIn { from { opacity: 0; } to { opacity: 1; } }
        .bp-intro-scrim {
          background: rgba(13, 36, 68, 0.55);
          backdrop-filter: blur(6px);
          -webkit-backdrop-filter: blur(6px);
          animation: bpIntroScrimIn 300ms ease-out;
        }
        @keyframes bpIntroCardIn {
          0% { transform: translateY(36px) scale(0.86) rotate(-1.5deg); opacity: 0; }
          60% { transform: translateY(-6px) scale(1.02) rotate(0.5deg); opacity: 1; }
          100% { transform: translateY(0) scale(1) rotate(0); opacity: 1; }
        }
        .bp-intro-card {
          box-shadow: 0 30px 70px rgba(6, 30, 64, 0.42);
          animation: bpIntroCardIn 540ms cubic-bezier(0.2, 0.9, 0.3, 1.1) both;
        }
        @keyframes bpIntroRise {
          0% { bottom: -30px; opacity: 0; transform: translateX(0); }
          20% { opacity: 0.9; }
          100% { bottom: 108%; opacity: 0; transform: translateX(12px); }
        }
        .bp-intro-rise { bottom: -30px; animation: bpIntroRise linear infinite; }
        @keyframes bpHeroBob {
          from { transform: translateY(-6px); }
          to { transform: translateY(6px); }
        }
        .bp-hero-bob { display: inline-flex; animation: bpHeroBob 1.8s ease-in-out infinite alternate; }
        @keyframes bpIntroItemIn {
          from { transform: translateY(14px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .bp-intro-item { animation: bpIntroItemIn 420ms ease-out both; }
        @keyframes bpLegendFloat {
          from { transform: translateY(-3px) rotate(-4deg); }
          to { transform: translateY(3px) rotate(4deg); }
        }
        .bp-legend-float { display: inline-flex; animation: bpLegendFloat 1.6s ease-in-out infinite alternate; }
        @keyframes bpCtaBreath {
          from { transform: scale(1); }
          to { transform: scale(1.02); }
        }
        .bp-intro-cta {
          animation: bpIntroItemIn 420ms ease-out 920ms both, bpCtaBreath 1.15s ease-in-out 1.5s infinite alternate;
        }
        @keyframes bpCtaArrow {
          from { transform: translateX(0); }
          to { transform: translateX(5px); }
        }
        .bp-cta-arrow { animation: bpCtaArrow 0.8s ease-in-out infinite alternate; }
        @media (prefers-reduced-motion: reduce) {
          .bp-intro-card, .bp-intro-rise, .bp-hero-bob, .bp-intro-item, .bp-legend-float,
          .bp-intro-cta, .bp-cta-arrow { animation: none; }
        }
      `}</style>
    </div>
  );
};

export default MathBubblePopPage;
